/**
 * The base colors for the  PRO Material.
 * You can add new color using this file.
 * You can customized the colors for the entire  PRO Material using thie file.
 */

// types
interface ColorsTypes {
  main: string
  focus: string
}

interface GradientsTypes {
  main: string
  state: string
}

interface SocialMediaColorsTypes {
  main: string
  dark: string
}

interface BadgeColorsTypes {
  background: string
  text: string
}

type Color = Record<0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9, string>

interface Types {
  background:
    | {
        default: string
        sidenav?: string
        card?: string
      }
    | any
  white:
    | {
        main: string
        focus: string
      }
    | any
  text:
    | {
        main: string
        focus: string
        primary?: string
        secondary?: string
        disabled?: string
      }
    | any
  transparent:
    | {
        main: string
      }
    | any
  black:
    | {
        light: string
        main: string
        focus: string
      }
    | any
  primary: ColorsTypes | any
  secondary: ColorsTypes | any
  info: ColorsTypes | any
  success: ColorsTypes | any
  warning: ColorsTypes | any
  error: ColorsTypes | any
  light: ColorsTypes | any
  dark: Color
  gray: Color
  blue: Color
  cyan: Color
  grape: Color
  green: Color
  indigo: Color
  lime: Color
  orange: Color
  pink: Color
  red: Color
  teal: Color
  violet: Color
  yellow: Color
  grey: Record<number, string>
  gradients:
    | {
        primary: GradientsTypes
        secondary: GradientsTypes
        info: GradientsTypes
        success: GradientsTypes
        warning: GradientsTypes
        error: GradientsTypes
        light: GradientsTypes
        dark: GradientsTypes
      }
    | any
  socialMediaColors:
    | {
        facebook: SocialMediaColorsTypes
        twitter: SocialMediaColorsTypes
        instagram: SocialMediaColorsTypes
        linkedin: SocialMediaColorsTypes
        pinterest: SocialMediaColorsTypes
        youtube: SocialMediaColorsTypes
        vimeo: SocialMediaColorsTypes
        slack: SocialMediaColorsTypes
        dribbble: SocialMediaColorsTypes
        github: SocialMediaColorsTypes
        reddit: SocialMediaColorsTypes
        tumblr: SocialMediaColorsTypes
      }
    | any
  badgeColors:
    | {
        primary: BadgeColorsTypes
        secondary: BadgeColorsTypes
        info: BadgeColorsTypes
        success: BadgeColorsTypes
        warning: BadgeColorsTypes
        error: BadgeColorsTypes
        light: BadgeColorsTypes
        dark: BadgeColorsTypes
      }
    | any
  coloredShadows:
    | {
        [key: string]: string
      }
    | any
  inputBorderColor: string
  tabs:
    | {
        indicator:
          | {
              boxShadow: string
            }
          | any
      }
    | any
}

const colors: Types = {
  background: {
    // The page is pure black and a surface is one step off it. Nothing in the
    // chrome carries a hue any more.
    default: '#000000',
    sidenav: '#121212',
    card: '#121212',
  },

  text: {
    main: '#ffffffcc',
    focus: '#ffffffcc',
    // MUI's Paper (and so every Card, Menu, Dialog and Table) paints
    // palette.text.primary on itself. It was never set, so MUI fell back to its
    // light-mode default -- near black -- and every surface in the app had to
    // undo it by hand. The app is dark, so the default is light.
    primary: '#ffffff',
    secondary: '#ffffffb3',
    disabled: '#ffffff80',
  },

  transparent: {
    main: 'transparent',
  },

  white: {
    main: '#ffffff',
    focus: '#ffffff',
  },

  black: {
    light: '#000000',
    main: '#000000',
    focus: '#000000',
  },

  primary: {
    // The primary action is a white pill with black type, so "primary" is white.
    main: '#ffffff',
    focus: '#e5e5e5',
  },

  secondary: {
    main: '#a3a3a3',
    focus: '#c7c7c7',
  },

  info: {
    // Was the brand blue; it dressed links and focus rings, which are now white.
    main: '#ffffff',
    focus: '#e5e5e5',
  },

  success: {
    main: '#4CAF50',
    focus: '#67bb6a',
  },

  warning: {
    main: '#fb8c00',
    focus: '#fc9d26',
  },

  error: {
    main: '#F44335',
    focus: '#f65f53',
  },

  light: {
    main: '#f0f2f566',
    focus: '#f0f2f566',
  },

  blue: {
    '0': '#eaf5fe',
    '1': '#d5eafd',
    '2': '#b0d7fb',
    '3': '#86bef7',
    '4': '#66a9f1',
    '5': '#5298e9',
    '6': '#4589df',
    '7': '#3d7cd0',
    '8': '#366fbc',
    '9': '#3163a6',
  },
  cyan: {
    '0': '#e7f9fb',
    '1': '#cff5f9',
    '2': '#abe7f0',
    '3': '#84d6e5',
    '4': '#68c6d8',
    '5': '#58b5cc',
    '6': '#4fa7bc',
    '7': '#4596aa',
    '8': '#3c8396',
    '9': '#337083',
  },
  dark: {
    '0': '#c1c2c5',
    '1': '#a6a7ab',
    '2': '#909296',
    '3': '#5d5f65',
    '4': '#383a3f',
    '5': '#2c2e33',
    '6': '#25262b',
    '7': '#1a1b1e',
    '8': '#141517',
    '9': '#101113',
  },
  grape: {
    '0': '#f7f0fb',
    '1': '#efdaf8',
    '2': '#e6c0f6',
    '3': '#da9cf1',
    '4': '#cd7ceb',
    '5': '#be63e1',
    '6': '#b052d4',
    '7': '#a146c2',
    '8': '#903daf',
    '9': '#7c3497',
  },
  gray: {
    '0': '#f8f9fa',
    '1': '#f1f3f5',
    '2': '#eaecef',
    '3': '#dfe2e6',
    '4': '#cfd4d9',
    '5': '#aeb5bc',
    '6': '#878e95',
    '7': '#4a5056',
    '8': '#353a3f',
    '9': '#222529',
  },
  green: {
    '0': '#eefaef',
    '1': '#daf8db',
    '2': '#bff0c0',
    '3': '#a2e7a1',
    '4': '#87d886',
    '5': '#75cc72',
    '6': '#66bb62',
    '7': '#5daf59',
    '8': '#529c4f',
    '9': '#488847',
  },
  indigo: {
    '0': '#eef2fe',
    '1': '#dde4fd',
    '2': '#bdc8fb',
    '3': '#95a6f9',
    '4': '#798ef5',
    '5': '#637bf2',
    '6': '#536ded',
    '7': '#4962e3',
    '8': '#425ad3',
    '9': '#3b4ec0',
  },
  lime: {
    '0': '#f5fce5',
    '1': '#ecf9cd',
    '2': '#def4ab',
    '3': '#c9ea84',
    '4': '#b5e164',
    '5': '#a3d64f',
    '6': '#92c744',
    '7': '#83b63c',
    '8': '#75a635',
    '9': '#69932e',
  },
  orange: {
    '0': '#fdf4e7',
    '1': '#fbe9cf',
    '2': '#f9d9ae',
    '3': '#f5c383',
    '4': '#f3ad5f',
    '5': '#f09745',
    '6': '#ed8537',
    '7': '#e6702e',
    '8': '#d7622b',
    '9': '#c95328',
  },
  pink: {
    '0': '#fcf1f6',
    '1': '#fadfea',
    '2': '#f3c4d6',
    '3': '#eda6c0',
    '4': '#e889ab',
    '5': '#df6e94',
    '6': '#d55580',
    '7': '#c5426c',
    '8': '#b2355c',
    '9': '#982c4d',
  },
  red: {
    '0': '#fdf5f5',
    '1': '#fae4e3',
    '2': '#f7cbca',
    '3': '#f3acaa',
    '4': '#ef8d8a',
    '5': '#ed7470',
    '6': '#e75f59',
    '7': '#dd4e47',
    '8': '#ce423a',
    '9': '#b93a33',
  },
  teal: {
    '0': '#eafbf5',
    '1': '#cef8e9',
    '2': '#abf0d8',
    '3': '#87e3c0',
    '4': '#6ed6ac',
    '5': '#5fc69a',
    '6': '#55b589',
    '7': '#4ba47b',
    '8': '#41906b',
    '9': '#387d5d',
  },
  violet: {
    '0': '#f2f0fe',
    '1': '#e3dbfc',
    '2': '#cdc0fa',
    '3': '#ad98f5',
    '4': '#9276f2',
    '5': '#7e60ee',
    '6': '#7352e9',
    '7': '#6a4ae0',
    '8': '#6243d1',
    '9': '#5a3ebd',
  },
  yellow: {
    '0': '#fef9de',
    '1': '#fdf3c5',
    '2': '#fceda3',
    '3': '#fae179',
    '4': '#f8d65b',
    '5': '#f3c648',
    '6': '#edb13f',
    '7': '#e9a339',
    '8': '#e29134',
    '9': '#d87d2e',
  },

  grey: {
    100: '#f8f9fa',
    200: '#f0f2f5',
    300: '#dee2e6',
    400: '#ced4da',
    500: '#adb5bd',
    600: '#6c757d',
    700: '#495057',
    800: '#343a40',
    900: '#212529',
  },

  gradients: {
    primary: {
      main: '#EC407A',
      state: '#D81B60',
    },

    secondary: {
      main: '#747b8a',
      state: '#495361',
    },

    info: {
      main: '#49a3f1',
      state: '#1A73E8',
    },

    success: {
      main: '#66BB6A',
      state: '#43A047',
    },

    warning: {
      main: '#FFA726',
      state: '#FB8C00',
    },

    error: {
      main: '#EF5350',
      state: '#E53935',
    },

    light: {
      main: '#EBEFF4',
      state: '#CED4DA',
    },

    dark: {
      main: '#323a54',
      state: '#1a2035',
    },
  },

  socialMediaColors: {
    facebook: {
      main: '#3b5998',
      dark: '#344e86',
    },

    twitter: {
      main: '#55acee',
      dark: '#3ea1ec',
    },

    instagram: {
      main: '#125688',
      dark: '#0e456d',
    },

    linkedin: {
      main: '#0077b5',
      dark: '#00669c',
    },

    pinterest: {
      main: '#cc2127',
      dark: '#b21d22',
    },

    youtube: {
      main: '#e52d27',
      dark: '#d41f1a',
    },

    vimeo: {
      main: '#1ab7ea',
      dark: '#13a3d2',
    },

    slack: {
      main: '#3aaf85',
      dark: '#329874',
    },

    dribbble: {
      main: '#ea4c89',
      dark: '#e73177',
    },

    github: {
      main: '#24292e',
      dark: '#171a1d',
    },

    reddit: {
      main: '#ff4500',
      dark: '#e03d00',
    },

    tumblr: {
      main: '#35465c',
      dark: '#2a3749',
    },
  },

  badgeColors: {
    primary: {
      background: '#f8b3ca',
      text: '#cc084b',
    },

    secondary: {
      background: '#d7d9e1',
      text: '#6c757d',
    },

    info: {
      background: '#aecef7',
      text: '#095bc6',
    },

    success: {
      background: '#bce2be',
      text: '#339537',
    },

    warning: {
      background: '#ffd59f',
      text: '#c87000',
    },

    error: {
      background: '#fcd3d0',
      text: '#f61200',
    },

    light: {
      background: '#ffffff',
      text: '#c7d3de',
    },

    dark: {
      background: '#8097bf',
      text: '#1e2e4a',
    },
  },

  coloredShadows: {
    primary: '#e91e62',
    secondary: '#110e0e',
    info: '#00bbd4',
    success: '#4caf4f',
    warning: '#ff9900',
    error: '#f44336',
    light: '#adb5bd',
    dark: '#404040',
  },

  // Fields are filled, never outlined.
  inputBorderColor: 'transparent',

  tabs: {
    indicator: { boxShadow: '#ddd' },
  },
}

export default colors
